
import Close from 'vue-material-design-icons/Close.vue'
import PencilIcon from 'vue-material-design-icons/Pencil.vue'

import NcButton from '@nextcloud/vue/dist/Components/NcButton.js'
import NcRichText from '@nextcloud/vue/dist/Components/NcRichText.js'

import AvatarWrapper from './AvatarWrapper/AvatarWrapper.vue'
import DefaultParameter from './MessagesList/MessagesGroup/Message/MessagePart/DefaultParameter.vue'
import FilePreview from './MessagesList/MessagesGroup/Message/MessagePart/FilePreview.vue'

import { ATTENDEE, AVATAR } from '../constants.js'
import { EventBus } from '../services/EventBus.js'
import { useChatExtrasStore } from '../stores/chatExtras.js'

export default {
	name: 'Quote',
	components: {
		AvatarWrapper,
		NcButton,
		Close,
		NcRichText,
		PencilIcon,
	},
	props: {
		actorId: {
			type: String,
			default: '',
		},
		/**
		 * The sender of the message to be replied to.
		 */
		actorType: {
			type: String,
			default: '',
		},
		/**
		 * The display name of the sender of the message.
		 */
		actorDisplayName: {
			type: String,
			default: '',
		},
		/**
		 * The text of the message to be replied to.
		 */
		message: {
			type: String,
			default: '',
		},
		/**
		 * The text of the message to be replied to.
		 */
		messageParameters: {
			type: [Array, Object],
			default: () => { return {} },
		},
		/**
		 * The message id of the message to be replied to.
		 */
		id: {
			type: Number,
			default: 0,
		},
		/**
		 * The conversation token of the message to be replied to.
		 */
		token: {
			type: String,
			default: '',
		},

		lastEditActorId: {
			type: String,
			default: '',
		},

		lastEditActorType: {
			type: String,
			default: '',
		},

		lastEditActorDisplayName: {
			type: String,
			default: '',
		},

		lastEditTimestamp: {
			type: Number,
			default: 0,
		},

		/**
		 * If the quote component is used in the `NewMessage` component we display
		 * the remove button.
		 */
		canCancel: {
			type: Boolean,
			default: false,
		},

		editMessage: {
			type: Boolean,
			default: false,
		},
	},

	setup() {
		const chatExtrasStore = useChatExtrasStore()

		return {
			AVATAR,
			chatExtrasStore,
		}
	},

	computed: {
		component() {
			return this.canCancel
				? { tag: 'div', link: undefined }
				: { tag: 'router-link', link: { hash: this.hash, params: { skipLeaveWarning: true } } }
		},

		/**
		 * The message actor display name.
		 *
		 * @return {string}
		 */
		getDisplayName() {
			const displayName = this.actorDisplayName.trim()

			if (displayName === '' && this.actorType === ATTENDEE.ACTOR_TYPE.GUESTS) {
				return t('spreed', 'Guest')
			}

			if (displayName === '') {
				return t('spreed', 'Deleted user')
			}

			return displayName
		},

		isFederatedUser() {
			return this.actorType === ATTENDEE.ACTOR_TYPE.FEDERATED_USERS
		},

		getRemoteServer() {
			return this.isFederatedUser ? '(' + this.actorId.split('@').pop() + ')' : ''
		},

		getLastEditor() {
			if (!this.lastEditTimestamp) {
				return ''
			} else if (this.lastEditActorId === this.actorId
				&& this.lastEditActorType === this.actorType) {
				// TRANSLATORS Edited by the author of the message themselves
				return t('spreed', '(edited)')
			} else if (this.lastEditActorId === this.$store.getters.getActorId()
				&& this.lastEditActorType === this.$store.getters.getActorType()) {
				return t('spreed', '(edited by you)')
			} else if (this.lastEditActorId === 'deleted_users'
						&& this.lastEditActorType === 'deleted_users') {
				return t('spreed', '(edited by a deleted user)')
			} else {
				return t('spreed', '(edited by {moderator})', { moderator: this.lastEditActorDisplayName })
			}
		},

		isOwnMessageQuoted() {
			return this.actorId === this.$store.getters.getActorId()
				&& this.actorType === this.$store.getters.getActorType()
		},

		isFileShareMessage() {
			return Object.keys(Object(this.messageParameters)).some(key => key.startsWith('file'))
		},

		isFileShareWithoutCaption() {
			return this.isFileShareMessage && this.message === '{file}'
		},

		richParameters() {
			const richParameters = {}
			Object.keys(this.messageParameters).forEach(function(p) {
				const type = this.messageParameters[p].type
				if (type === 'file') {
					richParameters[p] = {
						component: FilePreview,
						props: Object.assign({
							token: this.token,
							smallPreview: true,
							rowLayout: !this.messageParameters[p].mimetype.startsWith('image/'),
						}, this.messageParameters[p]),
					}
				} else {
					richParameters[p] = {
						component: DefaultParameter,
						props: this.messageParameters[p],
					}
				}
			}.bind(this))
			return richParameters
		},

		/**
		 * This is a simplified version of the last chat message.
		 * Parameters are parsed without markup (just replaced with the name),
		 * e.g. no avatars on mentions.
		 *
		 * @return {string} A simple message to show below the conversation name
		 */
		simpleQuotedMessage() {
			if (!this.id) {
				return t('spreed', 'The message has expired or has been deleted')
			}

			if (!Object.keys(this.messageParameters).length) {
				return this.message
			}

			const params = this.messageParameters
			let subtitle = this.message

			// We don't really use rich objects in the subtitle, instead we fall back to the name of the item
			Object.keys(params).forEach((parameterKey) => {
				subtitle = subtitle.replaceAll('{' + parameterKey + '}', params[parameterKey].name)
			})

			return subtitle
		},
		// Shorten the message to 250 characters and append three dots to the end of the
		// string. This is needed because on very wide screens, if the 250 characters
		// fit, the css rules won't ellipsize the text-overflow.
		shortenedQuoteMessage() {
			if (this.simpleQuotedMessage.length >= 250) {
				return this.simpleQuotedMessage.substring(0, 250) + '…'
			} else {
				return this.simpleQuotedMessage
			}
		},

		cancelQuoteLabel() {
			return t('spreed', 'Cancel quote')
		},

		hash() {
			return '#message_' + this.id
		},
	},
	methods: {
		handleAbort() {
			if (this.editMessage) {
				this.chatExtrasStore.removeMessageIdToEdit(this.token)
			} else {
				this.chatExtrasStore.removeParentIdToReply(this.token)
			}
			EventBus.$emit('focus-chat-input')
		},

		handleQuoteClick() {
			if (this.canCancel) {
				return
			}

			if (this.$route.hash === this.hash) {
				// Already on this message route, just trigger highlight
				EventBus.$emit('focus-message', this.id)
			}
		},
	},
}
